import { useState, useEffect } from 'react';
import { getUser, logout } from '../../services/auth';
import { useNavigate, Link } from 'react-router-dom';
import { FaUsers, FaClipboardList, FaCreditCard } from 'react-icons/fa';
import api from '../../services/api';

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalJobs: 0,
    activeJobs: 0,
    totalRevenue: 0,
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const currentUser = getUser();
    if (!currentUser || currentUser.role !== 'admin') {
      navigate('/login');
      return;
    }
    setUser(currentUser);
    loadStats();
  }, [navigate]);

  const loadStats = async () => {
    try {
      // Load users and jobs stats
      const [usersRes, jobsRes] = await Promise.all([
        api.get('/users').catch(() => ({ data: [] })),
        api.get('/jobs').catch(() => ({ data: { jobs: [] } })),
      ]);
      
      const users = Array.isArray(usersRes.data) ? usersRes.data : [];
      // API returns { jobs: [...], pagination: {...} } or just array
      const jobs = jobsRes.data?.jobs || (Array.isArray(jobsRes.data) ? jobsRes.data : []);
      
      setStats({
        totalUsers: users.length,
        totalJobs: jobs.length,
        activeJobs: jobs.filter(j => j.status === 'active').length,
        totalRevenue: jobs.reduce((sum, job) => sum + (job.hourlyRate || 0), 0),
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold gradient-bg bg-clip-text text-transparent">
            Nannity - Admin
          </h1>
          <div className="flex items-center gap-4">
            <Link
              to="/admin"
              className="px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
            >
              Admin Panel
            </Link>
            <span className="text-gray-600 dark:text-gray-300">
              {user?.firstName} {user?.lastName}
            </span>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
            >
              Odjavi se
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <h3 className="text-sm text-gray-500 mb-2">Ukupno korisnika</h3>
            <p className="text-3xl font-bold text-primary-600">{stats.totalUsers}</p>
          </div>
          <div className="card">
            <h3 className="text-sm text-gray-500 mb-2">Ukupno poslova</h3>
            <p className="text-3xl font-bold text-primary-600">{stats.totalJobs}</p>
          </div>
          <div className="card">
            <h3 className="text-sm text-gray-500 mb-2">Aktivni poslovi</h3>
            <p className="text-3xl font-bold text-primary-600">{stats.activeJobs}</p>
          </div>
          <div className="card">
            <h3 className="text-sm text-gray-500 mb-2">Ukupna zarada</h3>
            <p className="text-3xl font-bold text-primary-600">{stats.totalRevenue} RSD</p>
          </div>
        </div>

        <div className="card">
          <h2 className="text-2xl font-bold mb-4">Brzi linkovi</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <Link
              to="/admin"
              className="p-6 border-2 border-primary-200 rounded-xl hover:border-primary-500 hover:shadow-md transition-all text-center"
            >
              <div className="flex justify-center mb-2">
                <FaUsers className="text-4xl text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="font-semibold">Upravljanje korisnicima</h3>
            </Link>
            <Link
              to="/admin?tab=jobs"
              className="p-6 border-2 border-primary-200 rounded-xl hover:border-primary-500 hover:shadow-md transition-all text-center"
            >
              <div className="flex justify-center mb-2">
                <FaClipboardList className="text-4xl text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="font-semibold">Upravljanje poslovima</h3>
            </Link>
            <Link
              to="/admin?tab=payments"
              className="p-6 border-2 border-primary-200 rounded-xl hover:border-primary-500 hover:shadow-md transition-all text-center"
            >
              <div className="flex justify-center mb-2">
                <FaCreditCard className="text-4xl text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="font-semibold">Plaćanja</h3>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}

